import {
  FormEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Alert,
  Button,
  Card,
  Form,
  Modal,
  Spinner,
  Table,
} from "react-bootstrap";
import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Eye,
  KeyRound,
  LockKeyhole,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  UsersRound,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import api from "Common/api";
import "./platform-access.css";

type PlatformAccessMe = {
  userId: number;
  isSuperAdmin: boolean;
  roleName: string;
  permissions: string[];
};

type PlatformPermission = {
  id: number;
  permissionKey: string;
  name: string;
  description: string;
  category: string;
};

type PlatformRole = {
  id: number;
  name: string;
  slug: string;
  description?: string;
  isSystem: number;
  isActive: number;
  permissionCount: number;
  permissions: Array<{ id: number; permissionKey: string }>;
};

type PlatformStaff = {
  id: number;
  userId: number;
  roleId: number;
  roleName: string;
  roleSlug: string;
  isSuperAdmin: number;
  status: "active" | "inactive";
  firstName: string;
  lastName: string;
  email: string;
};

type AuditRecord = {
  id: number;
  actorUserId: number;
  action: string;
  targetType: string;
  targetId?: string;
  metadata?: string | Record<string, unknown>;
  createdAt: string;
  actorFirstName?: string;
  actorLastName?: string;
  actorEmail?: string;
};

type ErrorLogRecord = {
  id: number;
  module?: string;
  context?: string;
  errorMessage?: string;
  messageSource?: "recorded" | "payload" | "missing";
  errorStack?: string;
  payload?: unknown;
  createdAt: string;
};

type ErrorLogPagination = {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
};

const emptyStaffForm = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  roleId: "",
};
const emptyRoleForm = {
  name: "",
  description: "",
  permissionIds: [] as number[],
};
type AccessTab = "staff" | "roles" | "audit" | "errors";

function displayName(staff: PlatformStaff) {
  return (
    [staff.firstName, staff.lastName].filter(Boolean).join(" ") || staff.email
  );
}

function formatAction(value: string) {
  return value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatDateTime(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function PlatformAccessSkeleton() {
  return (
    <div
      className="platform-access-skeleton"
      role="status"
      aria-label="Loading platform access"
    >
      <span className="visually-hidden">Loading platform access…</span>
      <div className="platform-skeleton-toolbar app-skeleton" />
      <div className="platform-access-summary">
        {[0, 1, 2].map((item) => (
          <div
            className="platform-summary-card platform-summary-card--skeleton"
            key={item}
          >
            <span className="platform-skeleton-icon app-skeleton" />
            <span>
              <i className="platform-skeleton-label app-skeleton" />
              <i className="platform-skeleton-value app-skeleton" />
            </span>
          </div>
        ))}
      </div>
      <div className="platform-skeleton-workspace">
        <div className="platform-skeleton-tabs app-skeleton" />
        <div className="platform-skeleton-controls app-skeleton" />
        {[0, 1, 2, 3, 4].map((item) => (
          <div className="platform-skeleton-row app-skeleton" key={item} />
        ))}
      </div>
    </div>
  );
}

export default function PlatformAccess() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [me, setMe] = useState<PlatformAccessMe | null>(null);
  const [staff, setStaff] = useState<PlatformStaff[]>([]);
  const [roles, setRoles] = useState<PlatformRole[]>([]);
  const [permissions, setPermissions] = useState<PlatformPermission[]>([]);
  const [audit, setAudit] = useState<AuditRecord[]>([]);
  const [errorLogs, setErrorLogs] = useState<ErrorLogRecord[]>([]);
  const [errorLogModules, setErrorLogModules] = useState<string[]>([]);
  const [errorLogPagination, setErrorLogPagination] =
    useState<ErrorLogPagination>({
      page: 1,
      pageSize: 25,
      total: 0,
      totalPages: 1,
    });
  const [errorLogsLoading, setErrorLogsLoading] = useState(false);
  const [errorLogsError, setErrorLogsError] = useState("");
  const [selectedErrorLog, setSelectedErrorLog] =
    useState<ErrorLogRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState<Date | null>(null);
  const [error, setError] = useState("");
  const requestedTab = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState<AccessTab>(
    requestedTab === "roles" ||
      requestedTab === "audit" ||
      requestedTab === "errors"
      ? requestedTab
      : "staff",
  );
  const [staffSearch, setStaffSearch] = useState("");
  const [staffRoleFilter, setStaffRoleFilter] = useState("all");
  const [staffStatusFilter, setStaffStatusFilter] = useState("all");
  const [roleSearch, setRoleSearch] = useState("");
  const [auditSearch, setAuditSearch] = useState("");
  const [auditActionFilter, setAuditActionFilter] = useState("all");
  const [errorLogSearch, setErrorLogSearch] = useState("");
  const [errorLogModule, setErrorLogModule] = useState("all");
  const [permissionSearch, setPermissionSearch] = useState("");
  const [staffModalOpen, setStaffModalOpen] = useState(false);
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [staffForm, setStaffForm] = useState(emptyStaffForm);
  const [roleForm, setRoleForm] = useState(emptyRoleForm);
  const [editingRole, setEditingRole] = useState<PlatformRole | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [updatingStaffId, setUpdatingStaffId] = useState<number | null>(null);

  const canViewAudit = Boolean(
    me?.isSuperAdmin || me?.permissions.includes("AUDIT_VIEW"),
  );
  const canViewErrorLogs = Boolean(
    me?.isSuperAdmin || me?.permissions.includes("ERROR_LOG_VIEW"),
  );

  const changeTab = useCallback(
    (tab: AccessTab) => {
      setActiveTab(tab);
      const next = new URLSearchParams(searchParams);
      if (tab === "staff") next.delete("tab");
      else next.set("tab", tab);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const handleTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (
      event.key !== "ArrowLeft" &&
      event.key !== "ArrowRight" &&
      event.key !== "Home" &&
      event.key !== "End"
    )
      return;
    event.preventDefault();
    const tabs: AccessTab[] = ["staff", "roles"];
    if (canViewAudit) tabs.push("audit");
    if (canViewErrorLogs) tabs.push("errors");
    const currentIndex = tabs.indexOf(activeTab);
    const nextTab =
      event.key === "Home"
        ? tabs[0]
        : event.key === "End"
          ? tabs[tabs.length - 1]
          : tabs[
              (currentIndex +
                (event.key === "ArrowRight" ? 1 : -1) +
                tabs.length) %
                tabs.length
            ];
    changeTab(nextTab);
    requestAnimationFrame(() =>
      document.getElementById(`platform-tab-${nextTab}`)?.focus(),
    );
  };

  const loadData = useCallback(async (background = false) => {
    background ? setRefreshing(true) : setLoading(true);
    setError("");
    try {
      const meResponse: any = await api.get("/platform-access/me");
      const currentAccess = meResponse?.data as PlatformAccessMe;
      setMe(currentAccess);

      const [staffResponse, roleResponse, permissionResponse] =
        (await Promise.all([
          api.get("/platform-access/staff"),
          api.get("/platform-access/roles"),
          api.get("/platform-access/permissions"),
        ])) as any[];
      setStaff(staffResponse?.data || []);
      setRoles(roleResponse?.data || []);
      setPermissions(permissionResponse?.data || []);

      if (
        currentAccess.isSuperAdmin ||
        currentAccess.permissions.includes("AUDIT_VIEW")
      ) {
        const auditResponse: any = await api.get(
          "/platform-access/audit?limit=100",
        );
        setAudit(auditResponse?.data || []);
      }
      setLastSyncedAt(new Date());
    } catch (requestError: any) {
      setError(
        requestError?.response?.data?.message ||
          requestError?.message ||
          "Unable to load platform access settings.",
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const loadErrorLogs = useCallback(
    async (page = 1) => {
      if (!canViewErrorLogs) return;
      setErrorLogsLoading(true);
      setErrorLogsError("");
      try {
        const params = new URLSearchParams({
          page: String(page),
          pageSize: String(errorLogPagination.pageSize),
        });
        if (errorLogSearch.trim()) params.set("search", errorLogSearch.trim());
        if (errorLogModule !== "all") params.set("module", errorLogModule);
        const response: any = await api.get(
          `/platform-access/error-logs?${params.toString()}`,
        );
        setErrorLogs(response?.data?.items || []);
        setErrorLogModules(response?.data?.modules || []);
        setErrorLogPagination(
          response?.data?.pagination || {
            page,
            pageSize: errorLogPagination.pageSize,
            total: 0,
            totalPages: 1,
          },
        );
      } catch (requestError: any) {
        setErrorLogsError(
          requestError?.response?.data?.message ||
            requestError?.message ||
            "Unable to load error logs.",
        );
      } finally {
        setErrorLogsLoading(false);
      }
    },
    [
      canViewErrorLogs,
      errorLogModule,
      errorLogPagination.pageSize,
      errorLogSearch,
    ],
  );

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (activeTab !== "errors" || !canViewErrorLogs) return;
    const timeoutId = window.setTimeout(
      () => loadErrorLogs(errorLogPagination.page),
      errorLogSearch.trim() ? 350 : 0,
    );
    return () => window.clearTimeout(timeoutId);
  }, [
    activeTab,
    canViewErrorLogs,
    errorLogModule,
    errorLogPagination.page,
    errorLogPagination.pageSize,
    errorLogSearch,
    loadErrorLogs,
  ]);

  useEffect(() => {
    if (!loading && activeTab === "audit" && !canViewAudit) changeTab("staff");
    if (!loading && activeTab === "errors" && !canViewErrorLogs)
      changeTab("staff");
  }, [activeTab, canViewAudit, canViewErrorLogs, changeTab, loading]);

  useEffect(() => {
    const nextTab: AccessTab =
      requestedTab === "roles" ||
      requestedTab === "audit" ||
      requestedTab === "errors"
        ? requestedTab
        : "staff";
    if (
      nextTab !== activeTab &&
      (nextTab !== "audit" || canViewAudit || loading) &&
      (nextTab !== "errors" || canViewErrorLogs || loading)
    )
      setActiveTab(nextTab);
  }, [
    activeTab,
    canViewAudit,
    canViewErrorLogs,
    loading,
    requestedTab,
  ]);

  const permissionGroups = useMemo(() => {
    return permissions.reduce<Record<string, PlatformPermission[]>>(
      (groups, permission) => {
        (groups[permission.category] ||= []).push(permission);
        return groups;
      },
      {},
    );
  }, [permissions]);

  const limitedRoles = roles.filter(
    (role) => role.slug !== "super-admin" && role.isActive === 1,
  );
  const activeRoleCount = roles.filter((role) => role.isActive === 1).length;
  const activeStaffCount = staff.filter(
    (member) => member.status === "active",
  ).length;
  const inactiveStaffCount = staff.length - activeStaffCount;

  const filteredStaff = useMemo(() => {
    const query = staffSearch.trim().toLowerCase();
    return staff.filter((member) => {
      const matchesSearch =
        !query ||
        [displayName(member), member.email, member.roleName].some((value) =>
          String(value || "")
            .toLowerCase()
            .includes(query),
        );
      const matchesRole =
        staffRoleFilter === "all" ||
        (staffRoleFilter === "super-admin"
          ? Boolean(member.isSuperAdmin)
          : String(member.roleId) === staffRoleFilter);
      const matchesStatus =
        staffStatusFilter === "all" || member.status === staffStatusFilter;
      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [staff, staffRoleFilter, staffSearch, staffStatusFilter]);

  const roleAssignments = useMemo(
    () =>
      staff.reduce<Record<number, number>>((counts, member) => {
        counts[member.roleId] = (counts[member.roleId] || 0) + 1;
        return counts;
      }, {}),
    [staff],
  );

  const filteredRoles = useMemo(() => {
    const query = roleSearch.trim().toLowerCase();
    return roles.filter(
      (role) =>
        !query ||
        [role.name, role.description, role.slug].some((value) =>
          String(value || "")
            .toLowerCase()
            .includes(query),
        ),
    );
  }, [roleSearch, roles]);

  const auditActions = useMemo(
    () => Array.from(new Set(audit.map((record) => record.action))).sort(),
    [audit],
  );
  const filteredAudit = useMemo(() => {
    const query = auditSearch.trim().toLowerCase();
    return audit.filter((record) => {
      const actor =
        [record.actorFirstName, record.actorLastName]
          .filter(Boolean)
          .join(" ") ||
        record.actorEmail ||
        `User #${record.actorUserId}`;
      const matchesSearch =
        !query ||
        [record.action, actor, record.targetType, record.targetId].some(
          (value) =>
            String(value || "")
              .toLowerCase()
              .includes(query),
        );
      return (
        matchesSearch &&
        (auditActionFilter === "all" || record.action === auditActionFilter)
      );
    });
  }, [audit, auditActionFilter, auditSearch]);

  const visiblePermissionGroups = useMemo<
    Record<string, PlatformPermission[]>
  >(() => {
    const query = permissionSearch.trim().toLowerCase();
    if (!query) return permissionGroups;
    return Object.fromEntries(
      Object.entries(permissionGroups)
        .map(([category, items]) => [
          category,
          items.filter((permission) =>
            [
              permission.name,
              permission.description,
              permission.permissionKey,
              category,
            ].some((value) =>
              String(value || "")
                .toLowerCase()
                .includes(query),
            ),
          ),
        ])
        .filter(([, items]) => (items as PlatformPermission[]).length),
    ) as Record<string, PlatformPermission[]>;
  }, [permissionGroups, permissionSearch]);

  const submitStaff = async (event: FormEvent) => {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const response: any = await api.post("/platform-access/staff", {
        ...staffForm,
        roleId: Number(staffForm.roleId),
      });
      toast.success(response?.message || "Platform staff member created");
      setStaffModalOpen(false);
      setStaffForm(emptyStaffForm);
      await loadData(true);
    } catch (requestError: any) {
      toast.error(
        requestError?.response?.data?.message ||
          "Unable to create platform staff member",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const submitRole = async (event: FormEvent) => {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const response: any = editingRole
        ? await api.put(`/platform-access/roles/${editingRole.id}`, roleForm)
        : await api.post("/platform-access/roles", roleForm);
      toast.success(
        response?.message ||
          (editingRole ? "Platform role updated" : "Platform role created"),
      );
      setRoleModalOpen(false);
      setRoleForm(emptyRoleForm);
      setEditingRole(null);
      await loadData(true);
    } catch (requestError: any) {
      toast.error(
        requestError?.response?.data?.message ||
          "Unable to create platform role",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const openCreateRole = () => {
    setEditingRole(null);
    setRoleForm(emptyRoleForm);
    setPermissionSearch("");
    setRoleModalOpen(true);
  };

  const openEditRole = (role: PlatformRole) => {
    setEditingRole(role);
    setPermissionSearch("");
    setRoleForm({
      name: role.name,
      description: role.description || "",
      permissionIds: role.permissions.map((permission) => permission.id),
    });
    setRoleModalOpen(true);
  };

  const togglePermissionGroup = (items: PlatformPermission[]) => {
    const itemIds = items.map((permission) => permission.id);
    const allSelected = itemIds.every((id) =>
      roleForm.permissionIds.includes(id),
    );
    setRoleForm((current) => ({
      ...current,
      permissionIds: allSelected
        ? current.permissionIds.filter((id) => !itemIds.includes(id))
        : Array.from(new Set([...current.permissionIds, ...itemIds])),
    }));
  };

  const updateStaff = async (
    member: PlatformStaff,
    updates: { roleId?: number; status?: "active" | "inactive" },
  ) => {
    if (updatingStaffId === member.id) return;
    setUpdatingStaffId(member.id);
    try {
      const response: any = await api.put(
        `/platform-access/staff/${member.id}`,
        updates,
      );
      toast.success(response?.message || "Platform staff updated");
      await loadData(true);
    } catch (requestError: any) {
      toast.error(
        requestError?.response?.data?.message ||
          "Unable to update platform staff access",
      );
    } finally {
      setUpdatingStaffId(null);
    }
  };

  if (loading) {
    return (
      <div className="page-content platform-access-page">
        <PlatformAccessSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-content platform-access-page">
        <Alert variant="danger" className="platform-access-error">
          <Alert.Heading>Platform access unavailable</Alert.Heading>
          <p>{error}</p>
          <Button variant="outline-danger" onClick={() => loadData()}>
            Try again
          </Button>
        </Alert>
      </div>
    );
  }

  return (
    <div className="page-content platform-access-page">
      <div className="platform-access-shell">
        <div className="platform-page-toolbar">
          <div className="platform-page-heading">
            <span className="platform-page-eyebrow">Platform security</span>
            <h1>Access control</h1>
            <p>
              Manage platform-level staff, reusable access roles, and security
              activity.
            </p>
            <div className="platform-page-meta">
              <span className="platform-scope-pill">
                <ShieldCheck size={14} />{" "}
                {me?.isSuperAdmin ? "Super Admin" : me?.roleName} · Platform
                scope
              </span>
              {lastSyncedAt && (
                <span
                  className="platform-sync-status"
                  title={lastSyncedAt.toLocaleString()}
                >
                  <Clock3 size={13} /> Last synced{" "}
                  {lastSyncedAt.toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
              )}
            </div>
          </div>
          <Button
            variant="outline-secondary"
            className="platform-refresh-button"
            onClick={async () => {
              await loadData(true);
              if (activeTab === "errors" && canViewErrorLogs) {
                await loadErrorLogs(errorLogPagination.page);
              }
            }}
            disabled={refreshing}
          >
            {refreshing ? <Spinner size="sm" /> : <RefreshCw size={16} />}{" "}
            Refresh
          </Button>
        </div>

        <section
          className="platform-access-summary"
          aria-label="Platform access summary"
        >
          <button
            type="button"
            className="platform-summary-card"
            onClick={() => changeTab("staff")}
          >
            <span className="platform-summary-icon">
              <UsersRound size={19} />
            </span>
            <span className="platform-summary-copy">
              <small>Platform staff</small>
              <strong>{staff.length}</strong>
              <em>
                {activeStaffCount} active
                {inactiveStaffCount ? ` · ${inactiveStaffCount} inactive` : ""}
              </em>
            </span>
          </button>
          <button
            type="button"
            className="platform-summary-card"
            onClick={() => changeTab("roles")}
          >
            <span className="platform-summary-icon">
              <ShieldCheck size={19} />
            </span>
            <span className="platform-summary-copy">
              <small>Active roles</small>
              <strong>{activeRoleCount}</strong>
              <em>{roles.length} total roles</em>
            </span>
          </button>
          <button
            type="button"
            className="platform-summary-card"
            onClick={() => changeTab("roles")}
          >
            <span className="platform-summary-icon">
              <KeyRound size={19} />
            </span>
            <span className="platform-summary-copy">
              <small>Permissions</small>
              <strong>{permissions.length}</strong>
              <em>Across {Object.keys(permissionGroups).length} categories</em>
            </span>
          </button>
        </section>

        <Card className="platform-access-card">
          <div
            className="platform-access-tabs"
            role="tablist"
            aria-label="Platform access sections"
          >
            <button
              id="platform-tab-staff"
              role="tab"
              aria-selected={activeTab === "staff"}
              aria-controls="platform-panel-staff"
              tabIndex={activeTab === "staff" ? 0 : -1}
              className={activeTab === "staff" ? "is-active" : ""}
              onKeyDown={handleTabKeyDown}
              onClick={() => changeTab("staff")}
            >
              Platform staff <span>{staff.length}</span>
            </button>
            <button
              id="platform-tab-roles"
              role="tab"
              aria-selected={activeTab === "roles"}
              aria-controls="platform-panel-roles"
              tabIndex={activeTab === "roles" ? 0 : -1}
              className={activeTab === "roles" ? "is-active" : ""}
              onKeyDown={handleTabKeyDown}
              onClick={() => changeTab("roles")}
            >
              Roles & permissions <span>{roles.length}</span>
            </button>
            {canViewAudit && (
              <button
                id="platform-tab-audit"
                role="tab"
                aria-selected={activeTab === "audit"}
                aria-controls="platform-panel-audit"
                tabIndex={activeTab === "audit" ? 0 : -1}
                className={activeTab === "audit" ? "is-active" : ""}
                onKeyDown={handleTabKeyDown}
                onClick={() => changeTab("audit")}
              >
                Audit history <span>{audit.length}</span>
              </button>
            )}
            {canViewErrorLogs && (
              <button
                id="platform-tab-errors"
                role="tab"
                aria-selected={activeTab === "errors"}
                aria-controls="platform-panel-errors"
                tabIndex={activeTab === "errors" ? 0 : -1}
                className={activeTab === "errors" ? "is-active" : ""}
                onKeyDown={handleTabKeyDown}
                onClick={() => changeTab("errors")}
              >
                Error logs <span>{errorLogPagination.total}</span>
              </button>
            )}
          </div>

          {activeTab === "staff" && (
            <Card.Body
              id="platform-panel-staff"
              role="tabpanel"
              aria-labelledby="platform-tab-staff"
              className="p-0"
            >
              <div className="platform-panel-toolbar">
                <div className="platform-panel-heading">
                  <div>
                    <h2>Platform staff</h2>
                    <p>
                      Assign limited access without sharing unrestricted Super
                      Admin privileges.
                    </p>
                  </div>
                  {me?.isSuperAdmin && (
                    <Button onClick={() => setStaffModalOpen(true)}>
                      <Plus size={16} /> Add staff
                    </Button>
                  )}
                </div>
                <div className="platform-filter-row">
                  <div className="app-search-field platform-search-field">
                    <Search className="app-field-icon" aria-hidden="true" />
                    <Form.Control
                      value={staffSearch}
                      onChange={(event) => setStaffSearch(event.target.value)}
                      placeholder="Search name, email, or role"
                      aria-label="Search platform staff"
                    />
                  </div>
                  <Form.Select
                    value={staffRoleFilter}
                    onChange={(event) => setStaffRoleFilter(event.target.value)}
                    aria-label="Filter staff by role"
                  >
                    <option value="all">All roles</option>
                    <option value="super-admin">Super Admin</option>
                    {roles
                      .filter((role) => role.slug !== "super-admin")
                      .map((role) => (
                        <option key={role.id} value={String(role.id)}>
                          {role.name}
                        </option>
                      ))}
                  </Form.Select>
                  <Form.Select
                    value={staffStatusFilter}
                    onChange={(event) =>
                      setStaffStatusFilter(event.target.value)
                    }
                    aria-label="Filter staff by status"
                  >
                    <option value="all">All statuses</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </Form.Select>
                  <span className="platform-result-count">
                    <strong>{filteredStaff.length}</strong> of {staff.length}
                  </span>
                </div>
              </div>
              <div className="table-responsive">
                <Table hover className="platform-access-table mb-0">
                  <thead>
                    <tr>
                      <th>Staff member</th>
                      <th>Access role</th>
                      <th>Status</th>
                      <th>Scope</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStaff.map((member) => (
                      <tr key={member.id}>
                        <td>
                          <div className="platform-person">
                            <span className="platform-person-avatar">
                              {displayName(member).charAt(0).toUpperCase()}
                            </span>
                            <span>
                              <strong>{displayName(member)}</strong>
                              <small>{member.email}</small>
                            </span>
                          </div>
                        </td>
                        <td>
                          {member.isSuperAdmin ? (
                            <span className="platform-badge platform-badge--primary">
                              <LockKeyhole size={12} /> Super Admin
                            </span>
                          ) : me?.isSuperAdmin ? (
                            <Form.Select
                              value={member.roleId}
                              disabled={updatingStaffId === member.id}
                              onChange={(event) =>
                                updateStaff(member, {
                                  roleId: Number(event.target.value),
                                })
                              }
                              aria-label={`Role for ${displayName(member)}`}
                            >
                              {limitedRoles.map((role) => (
                                <option key={role.id} value={role.id}>
                                  {role.name}
                                </option>
                              ))}
                            </Form.Select>
                          ) : (
                            <span className="platform-badge platform-badge--neutral">
                              {member.roleName}
                            </span>
                          )}
                        </td>
                        <td>
                          {member.isSuperAdmin || !me?.isSuperAdmin ? (
                            <span
                              className={`platform-badge ${member.status === "active" ? "platform-badge--success" : "platform-badge--neutral"}`}
                            >
                              {member.status === "active"
                                ? "Active"
                                : "Inactive"}
                            </span>
                          ) : (
                            <div className="platform-status-control">
                              <Form.Check
                                type="switch"
                                checked={member.status === "active"}
                                disabled={updatingStaffId === member.id}
                                onChange={(event) =>
                                  updateStaff(member, {
                                    status: event.target.checked
                                      ? "active"
                                      : "inactive",
                                  })
                                }
                                aria-label={`${member.status === "active" ? "Deactivate" : "Activate"} ${displayName(member)}`}
                              />
                              <span>
                                {updatingStaffId === member.id
                                  ? "Updating…"
                                  : member.status === "active"
                                    ? "Active"
                                    : "Inactive"}
                              </span>
                            </div>
                          )}
                        </td>
                        <td>
                          <span className="platform-scope-copy">
                            <strong>
                              {member.isSuperAdmin
                                ? "Full platform"
                                : "Permission limited"}
                            </strong>
                            <small>
                              {member.isSuperAdmin
                                ? "Unrestricted system access"
                                : `Controlled by ${member.roleName}`}
                            </small>
                          </span>
                        </td>
                      </tr>
                    ))}
                    {!filteredStaff.length && (
                      <tr>
                        <td colSpan={4} className="platform-empty">
                          <strong>No staff found</strong>
                          <span>
                            {staff.length
                              ? "Try changing the search or filters."
                              : "Add a limited administrator to start delegating platform work."}
                          </span>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          )}

          {activeTab === "roles" && (
            <Card.Body
              id="platform-panel-roles"
              role="tabpanel"
              aria-labelledby="platform-tab-roles"
              className="p-0"
            >
              <div className="platform-panel-toolbar">
                <div className="platform-panel-heading">
                  <div>
                    <h2>Platform roles</h2>
                    <p>
                      Reusable permission sets make staff access predictable and
                      auditable.
                    </p>
                  </div>
                  {me?.isSuperAdmin && (
                    <Button onClick={openCreateRole}>
                      <Plus size={16} /> Create role
                    </Button>
                  )}
                </div>
                <div className="platform-filter-row platform-filter-row--roles">
                  <div className="app-search-field platform-search-field">
                    <Search className="app-field-icon" aria-hidden="true" />
                    <Form.Control
                      value={roleSearch}
                      onChange={(event) => setRoleSearch(event.target.value)}
                      placeholder="Search roles"
                      aria-label="Search platform roles"
                    />
                  </div>
                  <span className="platform-result-count">
                    <strong>{filteredRoles.length}</strong> of {roles.length}
                  </span>
                </div>
              </div>
              <div className="platform-role-grid">
                {filteredRoles.map((role) => (
                  <article key={role.id} className="platform-role-card">
                    <div className="platform-role-card__header">
                      <span className="platform-role-icon">
                        <ShieldCheck size={18} />
                      </span>
                      <div>
                        <h3>{role.name}</h3>
                        <span className="platform-role-slug">{role.slug}</span>
                      </div>
                      <span
                        className={`platform-badge ${role.isActive === 1 ? "platform-badge--success" : "platform-badge--neutral"}`}
                      >
                        {role.isActive === 1 ? "Active" : "Inactive"}
                      </span>
                    </div>
                    <p>{role.description || "No description provided."}</p>
                    <div className="platform-role-facts">
                      <span>
                        <strong>{role.permissionCount}</strong>
                        <small>Permissions</small>
                      </span>
                      <span>
                        <strong>{roleAssignments[role.id] || 0}</strong>
                        <small>Assigned staff</small>
                      </span>
                      <span>
                        <strong>
                          {role.slug === "super-admin" ? "Full" : "Limited"}
                        </strong>
                        <small>Scope</small>
                      </span>
                    </div>
                    <footer>
                      <span className="platform-system-note">
                        {role.isSystem === 1 ? (
                          <>
                            <LockKeyhole size={13} /> System role
                          </>
                        ) : (
                          "Custom role"
                        )}
                      </span>
                      <div className="platform-role-actions">
                        {me?.isSuperAdmin && role.slug !== "super-admin" && (
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => openEditRole(role)}
                          >
                            Edit permissions
                          </Button>
                        )}
                      </div>
                    </footer>
                  </article>
                ))}
                {!filteredRoles.length && (
                  <div className="platform-empty platform-empty--grid">
                    <strong>No roles found</strong>
                    <span>
                      {roles.length
                        ? "Try a different search."
                        : "Create a role to define limited platform access."}
                    </span>
                  </div>
                )}
              </div>
            </Card.Body>
          )}

          {activeTab === "audit" && canViewAudit && (
            <Card.Body
              id="platform-panel-audit"
              role="tabpanel"
              aria-labelledby="platform-tab-audit"
              className="p-0"
            >
              <div className="platform-panel-toolbar">
                <div className="platform-panel-heading">
                  <div>
                    <h2>Audit history</h2>
                    <p>
                      Review who changed platform access, what changed, and
                      when.
                    </p>
                  </div>
                </div>
                <div className="platform-filter-row">
                  <div className="app-search-field platform-search-field">
                    <Search className="app-field-icon" aria-hidden="true" />
                    <Form.Control
                      value={auditSearch}
                      onChange={(event) => setAuditSearch(event.target.value)}
                      placeholder="Search actor, target, or action"
                      aria-label="Search audit history"
                    />
                  </div>
                  <Form.Select
                    value={auditActionFilter}
                    onChange={(event) =>
                      setAuditActionFilter(event.target.value)
                    }
                    aria-label="Filter audit history by action"
                  >
                    <option value="all">All actions</option>
                    {auditActions.map((action) => (
                      <option key={action} value={action}>
                        {formatAction(action)}
                      </option>
                    ))}
                  </Form.Select>
                  <span className="platform-result-count">
                    <strong>{filteredAudit.length}</strong> of {audit.length}
                  </span>
                </div>
              </div>
              <div className="table-responsive">
                <Table hover className="platform-access-table mb-0">
                  <thead>
                    <tr>
                      <th>Action</th>
                      <th>Performed by</th>
                      <th>Target</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAudit.map((record) => (
                      <tr key={record.id}>
                        <td>
                          <strong>{formatAction(record.action)}</strong>
                          <span className="platform-audit-id">
                            Event #{record.id}
                          </span>
                        </td>
                        <td>
                          {[record.actorFirstName, record.actorLastName]
                            .filter(Boolean)
                            .join(" ") ||
                            record.actorEmail ||
                            `User #${record.actorUserId}`}
                        </td>
                        <td>
                          <span className="platform-badge platform-badge--neutral">
                            {formatAction(record.targetType)}{" "}
                            {record.targetId ? `#${record.targetId}` : ""}
                          </span>
                        </td>
                        <td>{formatDateTime(record.createdAt)}</td>
                      </tr>
                    ))}
                    {!filteredAudit.length && (
                      <tr>
                        <td colSpan={4} className="platform-empty">
                          <strong>No audit events found</strong>
                          <span>
                            {audit.length
                              ? "Try changing the search or action filter."
                              : "Access changes will appear here when they are recorded."}
                          </span>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          )}

          {activeTab === "errors" && canViewErrorLogs && (
            <Card.Body
              id="platform-panel-errors"
              role="tabpanel"
              aria-labelledby="platform-tab-errors"
              className="p-0"
            >
              <div className="platform-panel-toolbar">
                <div className="platform-panel-heading">
                  <div>
                    <h2>System error logs</h2>
                    <p>
                      Investigate sanitized application failures without
                      exposing credentials or personal data.
                    </p>
                  </div>
                </div>
                <div className="platform-filter-row">
                  <div className="app-search-field platform-search-field">
                    <Search className="app-field-icon" aria-hidden="true" />
                    <Form.Control
                      value={errorLogSearch}
                      onChange={(event) => {
                        setErrorLogSearch(event.target.value);
                        setErrorLogPagination((current) => ({
                          ...current,
                          page: 1,
                        }));
                      }}
                      placeholder="Search module, context, or message"
                      aria-label="Search system error logs"
                    />
                  </div>
                  <Form.Select
                    value={errorLogModule}
                    onChange={(event) => {
                      setErrorLogModule(event.target.value);
                      setErrorLogPagination((current) => ({
                        ...current,
                        page: 1,
                      }));
                    }}
                    aria-label="Filter error logs by module"
                  >
                    <option value="all">All modules</option>
                    {errorLogModules.map((moduleName) => (
                      <option key={moduleName} value={moduleName}>
                        {moduleName}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Select
                    value={errorLogPagination.pageSize}
                    onChange={(event) =>
                      setErrorLogPagination((current) => ({
                        ...current,
                        page: 1,
                        pageSize: Number(event.target.value),
                      }))
                    }
                    aria-label="Error logs per page"
                  >
                    <option value="10">10 per page</option>
                    <option value="25">25 per page</option>
                    <option value="50">50 per page</option>
                    <option value="100">100 per page</option>
                  </Form.Select>
                  <span className="platform-result-count">
                    <strong>{errorLogPagination.total}</strong> total
                  </span>
                </div>
              </div>

              {errorLogsError ? (
                <div className="platform-log-state platform-log-state--error">
                  <AlertTriangle size={18} aria-hidden="true" />
                  <span>
                    <strong>Error logs unavailable</strong>
                    {errorLogsError}
                  </span>
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() => loadErrorLogs(errorLogPagination.page)}
                  >
                    Try again
                  </Button>
                </div>
              ) : (
                <div className="table-responsive">
                  <Table hover className="platform-access-table platform-error-log-table mb-0">
                    <thead>
                      <tr>
                        <th>Error</th>
                        <th>Module</th>
                        <th>Context</th>
                        <th>Date</th>
                        <th aria-label="Actions" />
                      </tr>
                    </thead>
                    <tbody>
                      {errorLogsLoading ? (
                        [0, 1, 2, 3, 4].map((item) => (
                          <tr key={item} aria-hidden="true">
                            <td colSpan={5}>
                              <span className="platform-log-row-skeleton app-skeleton" />
                            </td>
                          </tr>
                        ))
                      ) : (
                        errorLogs.map((record) => (
                          <tr key={record.id}>
                            <td>
                              <strong>
                                {record.errorMessage ||
                                  "Error message unavailable"}
                              </strong>
                              <span className="platform-audit-id">
                                {record.messageSource === "payload"
                                  ? "Recovered from legacy payload · "
                                  : ""}
                                Log #{record.id}
                              </span>
                            </td>
                            <td>
                              <span className="platform-badge platform-badge--neutral">
                                {record.module || "Unknown"}
                              </span>
                            </td>
                            <td>
                              <span className="platform-log-context">
                                {record.context || "No context recorded"}
                              </span>
                            </td>
                            <td>{formatDateTime(record.createdAt)}</td>
                            <td className="platform-log-action">
                              <Button
                                size="sm"
                                variant="outline-secondary"
                                className="platform-log-view-button"
                                onClick={() => setSelectedErrorLog(record)}
                              >
                                <Eye size={14} aria-hidden="true" /> View
                              </Button>
                            </td>
                          </tr>
                        ))
                      )}
                      {!errorLogsLoading && !errorLogs.length && (
                        <tr>
                          <td colSpan={5} className="platform-empty">
                            <strong>No error logs found</strong>
                            <span>
                              {errorLogPagination.total
                                ? "Try changing the search or module filter."
                                : "New application failures will appear here after they are recorded."}
                            </span>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              )}

              {!errorLogsError && errorLogPagination.total > 0 && (
                <div className="platform-log-pagination">
                  <span>
                    Page <strong>{errorLogPagination.page}</strong> of{" "}
                    {errorLogPagination.totalPages}
                  </span>
                  <div>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      disabled={
                        errorLogsLoading || errorLogPagination.page <= 1
                      }
                      onClick={() =>
                        setErrorLogPagination((current) => ({
                          ...current,
                          page: Math.max(1, current.page - 1),
                        }))
                      }
                    >
                      <ChevronLeft size={14} aria-hidden="true" /> Previous
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      disabled={
                        errorLogsLoading ||
                        errorLogPagination.page >=
                          errorLogPagination.totalPages
                      }
                      onClick={() =>
                        setErrorLogPagination((current) => ({
                          ...current,
                          page: Math.min(
                            current.totalPages,
                            current.page + 1,
                          ),
                        }))
                      }
                    >
                      Next <ChevronRight size={14} aria-hidden="true" />
                    </Button>
                  </div>
                </div>
              )}
            </Card.Body>
          )}
        </Card>
      </div>

      <Modal
        show={Boolean(selectedErrorLog)}
        onHide={() => setSelectedErrorLog(null)}
        centered
        size="lg"
        className="platform-log-modal"
      >
        <Modal.Header closeButton>
          <div>
            <Modal.Title>Error log details</Modal.Title>
            <p className="platform-modal-subtitle">
              Sanitized diagnostic information for log #{selectedErrorLog?.id}
            </p>
          </div>
        </Modal.Header>
        <Modal.Body className="platform-log-detail">
          {selectedErrorLog && (
            <>
              <dl className="platform-log-facts">
                <div>
                  <dt>Module</dt>
                  <dd>{selectedErrorLog.module || "Unknown"}</dd>
                </div>
                <div>
                  <dt>Recorded</dt>
                  <dd>{formatDateTime(selectedErrorLog.createdAt)}</dd>
                </div>
                <div>
                  <dt>Context</dt>
                  <dd>{selectedErrorLog.context || "Not recorded"}</dd>
                </div>
              </dl>
              <section>
                <h3>Error message</h3>
                <p>
                  {selectedErrorLog.errorMessage ||
                    "Error message unavailable for this log."}
                </p>
                {selectedErrorLog.messageSource === "payload" && (
                  <span className="platform-log-recovered">
                    Recovered from sanitized legacy payload
                  </span>
                )}
              </section>
              <section>
                <h3>Stack trace</h3>
                <pre>
                  {selectedErrorLog.errorStack ||
                    "Stack trace unavailable for this legacy log."}
                </pre>
              </section>
              <section>
                <h3>Sanitized payload</h3>
                <pre>
                  {selectedErrorLog.payload == null
                    ? "No payload recorded."
                    : JSON.stringify(selectedErrorLog.payload, null, 2)}
                </pre>
              </section>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setSelectedErrorLog(null)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={staffModalOpen}
        onHide={() => !submitting && setStaffModalOpen(false)}
        centered
      >
        <Form onSubmit={submitStaff}>
          <Modal.Header closeButton={!submitting}>
            <div>
              <Modal.Title>Add platform staff</Modal.Title>
              <p className="platform-modal-subtitle">
                Create a limited administrator and assign their initial role.
              </p>
            </div>
          </Modal.Header>
          <Modal.Body className="platform-modal-body">
            <div className="platform-access-note">
              <LockKeyhole size={16} />
              <span>
                <strong>Limited access only</strong>Super Admin access cannot be
                assigned from this form.
              </span>
            </div>
            <div className="platform-name-grid">
              <Form.Group>
                <Form.Label>First name</Form.Label>
                <Form.Control
                  required
                  value={staffForm.firstName}
                  onChange={(event) =>
                    setStaffForm({
                      ...staffForm,
                      firstName: event.target.value,
                    })
                  }
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Last name</Form.Label>
                <Form.Control
                  required
                  value={staffForm.lastName}
                  onChange={(event) =>
                    setStaffForm({ ...staffForm, lastName: event.target.value })
                  }
                />
              </Form.Group>
            </div>
            <Form.Group>
              <Form.Label>Email address</Form.Label>
              <Form.Control
                required
                type="email"
                value={staffForm.email}
                onChange={(event) =>
                  setStaffForm({ ...staffForm, email: event.target.value })
                }
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>
                Phone <span className="text-muted">(optional)</span>
              </Form.Label>
              <Form.Control
                value={staffForm.phone}
                onChange={(event) =>
                  setStaffForm({ ...staffForm, phone: event.target.value })
                }
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Platform role</Form.Label>
              <Form.Select
                required
                value={staffForm.roleId}
                onChange={(event) =>
                  setStaffForm({ ...staffForm, roleId: event.target.value })
                }
              >
                <option value="">Select a role</option>
                {limitedRoles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="outline-secondary"
              onClick={() => setStaffModalOpen(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting && <Spinner size="sm" />} Create staff account
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal
        show={roleModalOpen}
        onHide={() => {
          if (!submitting) {
            setRoleModalOpen(false);
            setEditingRole(null);
            setPermissionSearch("");
          }
        }}
        centered
        size="lg"
        className="platform-role-modal"
      >
        <Form onSubmit={submitRole}>
          <Modal.Header closeButton={!submitting}>
            <div>
              <Modal.Title>
                {editingRole ? "Edit platform role" : "Create platform role"}
              </Modal.Title>
              <p className="platform-modal-subtitle">
                Define a reusable set of permissions for limited platform staff.
              </p>
            </div>
          </Modal.Header>
          <Modal.Body className="platform-modal-body">
            <Form.Group>
              <Form.Label>Role name</Form.Label>
              <Form.Control
                required
                minLength={2}
                value={roleForm.name}
                onChange={(event) =>
                  setRoleForm({ ...roleForm, name: event.target.value })
                }
                placeholder="e.g. Compliance reviewer"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={roleForm.description}
                onChange={(event) =>
                  setRoleForm({ ...roleForm, description: event.target.value })
                }
              />
            </Form.Group>
            <div className="platform-permission-section">
              <div className="platform-permission-toolbar">
                <div>
                  <Form.Label>Permissions</Form.Label>
                  <span>
                    {roleForm.permissionIds.length} of {permissions.length}{" "}
                    selected
                  </span>
                </div>
                <div className="app-search-field">
                  <Search className="app-field-icon" aria-hidden="true" />
                  <Form.Control
                    value={permissionSearch}
                    onChange={(event) =>
                      setPermissionSearch(event.target.value)
                    }
                    placeholder="Search permissions"
                    aria-label="Search permissions"
                  />
                </div>
              </div>
              <div className="platform-permission-groups">
                {Object.entries(visiblePermissionGroups).map(
                  ([category, items]) => {
                    const allSelected = items.every((permission) =>
                      roleForm.permissionIds.includes(permission.id),
                    );
                    return (
                      <fieldset key={category}>
                        <div className="platform-permission-group-heading">
                          <legend>{category}</legend>
                          <button
                            type="button"
                            onClick={() => togglePermissionGroup(items)}
                          >
                            {allSelected ? "Clear category" : "Select category"}
                          </button>
                        </div>
                        {items.map((permission) => (
                          <Form.Check
                            key={permission.id}
                            id={`platform-permission-${permission.id}`}
                            type="checkbox"
                            checked={roleForm.permissionIds.includes(
                              permission.id,
                            )}
                            onChange={(event) =>
                              setRoleForm({
                                ...roleForm,
                                permissionIds: event.target.checked
                                  ? [...roleForm.permissionIds, permission.id]
                                  : roleForm.permissionIds.filter(
                                      (id) => id !== permission.id,
                                    ),
                              })
                            }
                            label={
                              <span>
                                <strong>{permission.name}</strong>
                                <small>{permission.description}</small>
                              </span>
                            }
                          />
                        ))}
                      </fieldset>
                    );
                  },
                )}
                {!Object.keys(visiblePermissionGroups).length && (
                  <div className="platform-empty platform-empty--permissions">
                    <strong>No permissions found</strong>
                    <span>Try a different search term.</span>
                  </div>
                )}
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <span className="platform-modal-selection-count">
              {roleForm.permissionIds.length} permissions selected
            </span>
            <Button
              variant="outline-secondary"
              onClick={() => {
                setRoleModalOpen(false);
                setEditingRole(null);
                setPermissionSearch("");
              }}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting && <Spinner size="sm" />}{" "}
              {editingRole ? "Save changes" : "Create role"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}
